import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import Navbar from './Navbar';
import { toast } from 'react-toastify';

function AuthPage({ userData, setUserData }) {
  const [mode, setMode] = useState('signup');
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    password: '',
    confirmPassword: ''
  });

  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpRefs = useRef([]);
  const [showOtpModal, setShowOtpModal] = useState(false);

  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get navigation state
  const navState = location.state || {};
  const { email: navEmail, mode: navMode, message, user: navUser } = navState;

  // Initialize with navigation state
  useEffect(() => {
    if (navEmail) {
      setUserData(prev => ({ ...prev, email: navEmail }));
    }
    
    if (navMode === 'otp') {
      // Direct to OTP modal
      setTimeout(() => {
        setShowOtpModal(true);
        if (message) toast.info(message);
      }, 500);
    } else if (navMode === 'login') {
      setMode('login');
      if (message) toast.info(message);
    } else {
      setMode('signup');
    }

    // Prefill form data if unverified user returning
    if (navUser && navMode === 'otp') {
      setFormData(prev => ({
        ...prev,
        fullName: navUser.fullName || '',
        companyName: navUser.companyName || '',
        password: '',
        confirmPassword: ''
      }));
    }
  }, [navEmail, navMode, message, navUser, setUserData]);

  // Clear form data when switching modes
  useEffect(() => {
    setFormData({
      fullName: '',
      companyName: '',
      password: '',
      confirmPassword: ''
    });
  }, [mode]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // OTP HANDLERS
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  // SIGNUP
  const handleSignup = async (e) => {
    e.preventDefault();

    setValidationError('');
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.signup, {
        fullName: formData.fullName,
        companyName: formData.companyName || undefined,
        email: userData.email || navEmail,
        password: formData.password
      });

      if (response.data?.status === 'UNVERIFIED_ACCOUNT') {
        setFormData(prev => ({
          ...prev,
          fullName: response.data.user?.fullName || '',
          companyName: response.data.user?.companyName || '',
          password: '',
          confirmPassword: ''
        }));
        toast.info('Please verify your email to continue.');
      }

      setShowOtpModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setValidationError('');
    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.signin, {
        email: userData.email || navEmail,
        password: formData.password,
        rememberMe: true
      });

      if (response.data.needsVerification) {
        setShowOtpModal(true);
        return;
      }

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('holaUser', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        window.location.href = '/subscribe';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // VERIFY OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.verifyOtp, {
        email: userData.email || navEmail,
        otp: otp.join("")
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('holaUser', JSON.stringify(response.data.user));
        toast.success('Account verified!');
        // Clear temp data
        localStorage.removeItem('tempEmail');
        window.location.href = '/subscribe';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP.');
    } finally {
      setLoading(false);
    }
  };

  // RESEND OTP
  const handleResendOtp = async () => {
    try {
      await axios.post(API_ENDPOINTS.resendOtp, { email: userData.email || navEmail });
      toast.success('OTP resent successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    }
  };

  // OTP MODAL
  if (showOtpModal) {
    return (
      <>
        <Navbar />
        <div className="container py-5 d-flex align-items-center" style={{ minHeight: '80vh' }}>
          <div className="row justify-content-center w-100">
            <div className="col-md-6">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  <div className="text-center mb-4">
                    <h2 className="fw-bold">Verify Your Email</h2>
                    <p className="text-muted">
                      We've sent a 4-digit code to <strong>{userData.email || navEmail}</strong>.
                    </p>
                    <p className="text-muted small">
                      Please check your <strong>spam or junk folder</strong> if you don't see the email.
                    </p>
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}

                  <form onSubmit={handleVerifyOtp}>
                    <div className="d-flex justify-content-center gap-2 mb-3">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpRefs.current[index] = el)}
                          type="text"
                          className="form-control text-center"
                          style={{ width: 60, height: 60, fontSize: '1.8rem', fontWeight: 600 }}
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          required
                        />
                      ))}
                    </div>

                    <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                      {loading ? 'Verifying...' : 'Verify & Continue'}
                    </button>
                  </form>

                  <div className="text-center mt-3">
                    <p className="text-muted mb-2">Didn't receive the code?</p>
                    <button className="btn btn-link" onClick={handleResendOtp} disabled={loading}>
                      Resend OTP
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // MAIN AUTH PAGE
  return (
    <>
      <Navbar />
      <div className="container py-5 d-flex align-items-center" style={{ minHeight: '80vh' }}>
        <div className="row justify-content-center w-100">
          <div className="col-md-6">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5">

                <div className="auth-tabs">
                  <button 
                    className={mode === 'signup' ? 'active' : ''} 
                    onClick={() => setMode('signup')}
                  >
                    Sign Up
                  </button>
                  <button 
                    className={mode === 'login' ? 'active' : ''} 
                    onClick={() => setMode('login')}
                  >
                    Log In
                  </button>
                </div>

                {validationError && <div className="alert alert-warning">{validationError}</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                
                {/* SIGNUP FORM */}
                {mode === 'signup' && (
                  <form onSubmit={handleSignup}>
                    <h2 className="fw-bold mb-2">Create Account</h2>
                    <p className="text-muted mb-4">Join Hola and start your journey</p>

                    <div className="mb-3">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        className="form-control"
                        placeholder="John Doe"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Company Name (Optional)</label>
                      <input
                        type="text"
                        name="companyName"
                        className="form-control"
                        placeholder="Your Company"
                        value={formData.companyName}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        value={userData.email || navEmail || ''}
                        disabled
                        className="form-control input-disabled"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password *</label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          className="form-control"
                          placeholder="Min 8 characters"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          minLength="8"
                        />
                        <span
                          className="input-group-text"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm Password *</label>
                      <div className="input-group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          className="form-control"
                          placeholder="Re-enter password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required
                        />
                        <span
                          className="input-group-text"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                        </span>
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary btn-lg w-100 mt-3" disabled={loading}>
                      {loading ? 'Creating Account...' : 'Sign Up'}
                    </button>
                  </form>
                )}

                {/* LOGIN FORM */}
                {mode === 'login' && (
                  <form onSubmit={handleLogin}>
                    <h2 className="fw-bold mb-2">Welcome Back</h2>
                    <p className="text-muted mb-4">Login to continue to Hola</p>

                    <div className="mb-3">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        value={userData.email || navEmail || ''}
                        disabled
                        className="form-control input-disabled"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          className="form-control"
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                        <span
                          className="input-group-text"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                        </span>
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary btn-lg w-100 mt-3" disabled={loading}>
                      {loading ? 'Logging in...' : 'Log In'}
                    </button>

                    <div className="text-center mt-3">
                      <a href="#forgot" className="text-primary text-decoration-none">
                        Forgot Password?
                      </a>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthPage;
